import { Component } from '@theme/component';

/** Consecutive frames the measured height must repeat before we stop measuring. */
const SETTLE_STABLE_FRAMES = 5;

/** Upper bound on a measuring pass, so a never-settling document cannot spin forever. */
const SETTLE_TIMEOUT_MS = 2000;

/**
 * NewsletterModal - modal shell that renders merchant-provided custom HTML.
 * Pure open/close behavior: it does not validate or submit anything. Whatever
 * markup/scripts the custom HTML carries are responsible for their own logic.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog element
 * @property {HTMLButtonElement} closeBtn - The close button
 *
 * @extends Component<Refs>
 */
export default class NewsletterModal extends Component {
  #triggerButton = null;
  #closeTimeoutId = null;
  #autoOpenTimeoutId = null;
  #wasAutoOpened = false;
  #resizeObserver = null;
  #mutationObserver = null;
  #measuring = false;
  #settleFrameId = null;
  #settleDeadline = 0;
  #settleStableFrames = 0;
  #settleLastHeight = -1;
  #boundHandleDialogClick = null;
  #boundHandleKeyDown = null;
  #boundSyncFrameHeight = null;
  #boundScheduleMeasure = null;
  #boundSettleStep = null;

  /** @type {string[]} */
  requiredRefs = ['dialog', 'closeBtn'];

  connectedCallback() {
    super.connectedCallback();

    this.#triggerButton = this.closest('.newsletter')?.querySelector('.newsletter__button');

    this.#boundHandleDialogClick = this.#handleDialogClick.bind(this);
    this.#boundHandleKeyDown = this.#handleKeyDown.bind(this);

    // Auto-size the isolated iframe to its content (optional ref).
    const { frame } = this.refs;
    if (frame) {
      this.#boundSyncFrameHeight = this.#syncFrameHeight.bind(this);
      frame.addEventListener('load', this.#boundSyncFrameHeight);
    }

    // Auto-open on first visit (no cookie consent yet).
    this.#autoOpenTimeoutId = window.setTimeout(() => {
      this.#autoOpen();
      this.#autoOpenTimeoutId = null;
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#closeTimeoutId) {
      clearTimeout(this.#closeTimeoutId);
      this.#closeTimeoutId = null;
    }
    if (this.#autoOpenTimeoutId) {
      clearTimeout(this.#autoOpenTimeoutId);
      this.#autoOpenTimeoutId = null;
    }
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#settleFrameId !== null) {
      cancelAnimationFrame(this.#settleFrameId);
      this.#settleFrameId = null;
    }
    this.refs.dialog?.removeEventListener('click', this.#boundHandleDialogClick);
    this.removeEventListener('keydown', this.#boundHandleKeyDown);
    if (this.#boundSyncFrameHeight) {
      this.refs.frame?.removeEventListener('load', this.#boundSyncFrameHeight);
    }
    if (this.#boundScheduleMeasure) {
      this.refs.frame?.contentDocument?.removeEventListener('load', this.#boundScheduleMeasure, true);
    }
  }

  /**
   * Syncs the iframe height to its content. Same-origin srcdoc, so the
   * content document is readable. Keeps watching for inner size changes.
   */
  #syncFrameHeight() {
    const { frame } = this.refs;
    const doc = frame?.contentDocument;
    if (!doc?.body) return;

    this.#boundScheduleMeasure ??= this.#scheduleMeasure.bind(this);
    this.#boundSettleStep ??= this.#settleStep.bind(this);

    this.#scheduleMeasure();

    // Only the body is observed. documentElement IS the frame's own viewport,
    // so observing it would re-fire on every height we write; Chrome detects
    // that loop and starts dropping notifications, which used to freeze the
    // height at a stale mid-animation value.
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#boundScheduleMeasure);
    this.#resizeObserver.observe(doc.body);

    // The custom HTML replaces its whole container on success, which resizes
    // nothing the observer above is watching until the new subtree settles.
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = new MutationObserver(this.#boundScheduleMeasure);
    this.#mutationObserver.observe(doc.body, { childList: true, subtree: true });

    // Images swapped in resolve later, and load does not bubble.
    doc.addEventListener('load', this.#boundScheduleMeasure, true);
  }

  /**
   * Starts (or extends) a measuring pass that runs until the content height
   * holds steady.
   *
   * A single measurement is never enough: the custom HTML animates its
   * container for ~0.5s after swapping in the success screen, so any one-shot
   * read lands on an intermediate height. Rather than guess a delay, we keep
   * measuring each frame and stop once the result stops moving, which also
   * covers images that decode late and fonts that swap in.
   */
  #scheduleMeasure() {
    this.#settleStableFrames = 0;

    // Already settling: resetting the counter above is enough, and it keeps the
    // hard deadline anchored to the start so this can never run unbounded.
    if (this.#settleFrameId !== null) return;

    this.#settleDeadline = performance.now() + SETTLE_TIMEOUT_MS;
    this.#settleFrameId = requestAnimationFrame(this.#boundSettleStep);
  }

  /**
   * One frame of the measuring pass. Stops once the height repeats for
   * {@link SETTLE_STABLE_FRAMES} frames, or when the deadline expires.
   */
  #settleStep() {
    const height = this.#measureFrame();

    if (height === this.#settleLastHeight) {
      this.#settleStableFrames += 1;
    } else {
      this.#settleStableFrames = 0;
      this.#settleLastHeight = height;
    }

    if (this.#settleStableFrames >= SETTLE_STABLE_FRAMES || performance.now() > this.#settleDeadline) {
      this.#settleFrameId = null;
      return;
    }

    this.#settleFrameId = requestAnimationFrame(this.#boundSettleStep);
  }

  /**
   * Measures the frame's content and applies it as the frame height.
   *
   * Two traps live here. First, `scrollHeight` can never report less than the
   * frame's own viewport, so it acted as a ratchet that only grew and left the
   * tall form's height behind the shorter success screen; we read the body box
   * instead, which tracks content and is free to shrink.
   *
   * Second, `vh` inside an iframe resolves against the iframe, not the browser
   * window — so any `vh` in the custom HTML feeds the height we write straight
   * back into the height we measure. Chasing that fixed point diverges toward
   * zero, so we break the cycle: the frame is parked at a constant reference
   * while measuring, and that same reference is published as `--modal-vh` for
   * the custom HTML to use in place of `vh`.
   *
   * @returns {number} The applied height in pixels, or -1 if it could not measure.
   */
  #measureFrame() {
    const { frame } = this.refs;
    const doc = frame?.contentDocument;
    const view = doc?.defaultView;
    if (!doc?.body || !view || this.#measuring) return -1;

    this.#measuring = true;

    const reference = Math.round(window.innerHeight);
    doc.documentElement.style.setProperty('--modal-vh', `${reference}px`);
    if (frame.style.height !== `${reference}px`) frame.style.height = `${reference}px`;

    const styles = view.getComputedStyle(doc.body);
    const margins = (parseFloat(styles.marginTop) || 0) + (parseFloat(styles.marginBottom) || 0);
    const measured = Math.ceil(doc.body.getBoundingClientRect().height + margins);
    const height = measured > 0 ? measured : reference;

    frame.style.height = `${height}px`;

    this.#measuring = false;

    return height;
  }

  #getFocusableElements() {
    const { dialog } = this.refs;
    if (!dialog) return [];

    return [...dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (element) => !element.hasAttribute('disabled') && !element.hasAttribute('hidden')
    );
  }

  /**
   * Opens the modal automatically only while the user has NOT accepted cookies.
   * Gated on the shared `ae_cookie_consent` cookie written by the cookie banner
   * (same pattern as GT with Avada): once consent is stored, the modal stops
   * auto-opening until that cookie expires. We also honor a short-lived
   * `ae_newsletter_dismissed` cookie set on close so it does not re-nag.
   */
  #autoOpen() {
    const cookies = document.cookie.split(';').map((c) => c.trim());
    const hasConsent = cookies.some((c) => c.startsWith('ae_cookie_consent='));
    const dismissed = cookies.some((c) => c.startsWith('ae_newsletter_dismissed='));
    if (hasConsent || dismissed) return;

    this.#wasAutoOpened = true;
    this.openModal();
  }

  /**
   * Opens the modal dialog.
   */
  openModal() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    dialog.showModal();
    dialog.classList.remove('newsletter-modal__dialog--closing');
    dialog.classList.add('newsletter-modal__dialog--opening');

    dialog.addEventListener('click', this.#boundHandleDialogClick);
    this.addEventListener('keydown', this.#boundHandleKeyDown);

    this.#closeTimeoutId = window.setTimeout(() => {
      dialog.classList.remove('newsletter-modal__dialog--opening');
      this.#closeTimeoutId = null;
    }, 140);

    requestAnimationFrame(() => {
      this.#syncFrameHeight();
      // Focus a real control if one exists; otherwise move focus to the modal
      // surface (not the close button) so no focus ring shows around the X.
      const firstFocusable = this.#getFocusableElements().find((element) => element !== this.refs.closeBtn);
      (firstFocusable ?? this.refs.surface ?? this.refs.closeBtn)?.focus();
    });
  }

  /**
   * Closes the modal dialog.
   */
  closeModal() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;
    if (dialog.classList.contains('newsletter-modal__dialog--closing')) return;

    // Suppress the modal for 1 hour after any close; it reappears once expired.
    document.cookie = 'ae_newsletter_dismissed=1; max-age=3600; path=/';

    dialog.removeEventListener('click', this.#boundHandleDialogClick);
    this.removeEventListener('keydown', this.#boundHandleKeyDown);

    dialog.classList.remove('newsletter-modal__dialog--opening');
    dialog.classList.add('newsletter-modal__dialog--closing');

    this.#closeTimeoutId = window.setTimeout(() => {
      dialog.close();
      dialog.classList.remove('newsletter-modal__dialog--closing');
      this.#closeTimeoutId = null;
      // Only return focus to the trigger if the user opened the modal manually.
      if (!this.#wasAutoOpened) {
        this.#triggerButton?.focus();
      }
      this.#wasAutoOpened = false;
    }, 140);
  }

  /**
   * Handles dialog click events (backdrop click detection).
   * @param {MouseEvent} event
   */
  #handleDialogClick(event) {
    const { dialog } = this.refs;
    if (!dialog || event.target !== dialog) return;

    const surface = dialog.querySelector('.newsletter-modal__surface');
    if (!surface || !(event instanceof MouseEvent)) {
      this.closeModal();
      return;
    }

    const { left, right, top, bottom } = surface.getBoundingClientRect();
    const clickedBackdrop =
      event.clientX < left ||
      event.clientX > right ||
      event.clientY < top ||
      event.clientY > bottom;

    if (clickedBackdrop) {
      this.closeModal();
    }
  }

  /**
   * Handles keydown events (focus trap + ESC to close).
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key === 'Tab') {
      const focusableElements = this.#getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeModal();
  }
}

if (!customElements.get('newsletter-modal')) {
  customElements.define('newsletter-modal', NewsletterModal);
}
